import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { translations } from '../translations';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';

type Language = 'en' | 'ar';
type Direction = 'ltr' | 'rtl';

interface LanguageState {
  language: Language;
  direction: Direction;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  setLanguage: (language: Language) => void;
  setDirection: (direction: Direction) => void;
  t: (key: string) => string;
}

export const useLanguageStore = create<LanguageState>()(
  persist<LanguageState, [], [], any>(
    (set, get) => ({
      language: 'en',
      direction: 'ltr',
      initializedUserId: null,
      cachedUserId: null,

      // Load-on-login from the shared user_preferences row (language/direction).
      initialize: async () => {
        const at = accountGeneration();
        const res = await fetchUserPreferences();
        // An answer for an account that has since signed out is not this copy's.
        if (at !== accountGeneration()) return;
        if (!res) return; // logged out
        if (get().initializedUserId === res.userId) return;
        set((state) => {
          const cacheIsOurs =
            state.cachedUserId === null || state.cachedUserId === res.userId;
          const cloudLang = res.row?.language;
          const cloudDir = res.row?.direction;
          const language: Language =
            cloudLang === 'en' || cloudLang === 'ar'
              ? cloudLang
              : cacheIsOurs
              ? state.language
              : 'en';
          const direction: Direction =
            cloudDir === 'ltr' || cloudDir === 'rtl'
              ? cloudDir
              : cacheIsOurs
              ? state.direction
              : 'ltr';
          return { language, direction, initializedUserId: res.userId, cachedUserId: res.userId };
        });
      },

      setLanguage: (language) => {
        set((state) => ({
          language,
          // Only auto-set direction when changing to/from Arabic if user hasn't manually set it
          direction: state.direction === 'ltr' && language === 'ar' ? 'rtl' : state.direction
        }));
        const { language: lang, direction } = get();
        void saveUserPreference({ language: lang, direction });
      },
      setDirection: (direction) => {
        set({ direction });
        void saveUserPreference({ direction });
      },
      t: (key: string) => {
        const { language } = get();
        return (translations[language] as Record<string, string>)[key] || key;
      }
    }),
    {
      name: 'language-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 2,
      partialize: (state: LanguageState) => ({
        language: state.language,
        direction: state.direction,
        cachedUserId: state.cachedUserId,
      }),
      migrate: (persistedState: any, version: number) => {
        if (version === 1) {
          return {
            ...persistedState,
            direction: persistedState.language === 'ar' ? 'rtl' : 'ltr'
          };
        }
        return persistedState as LanguageState;
      }
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage) — the language is the ACCOUNT's choice, not the
   device's: it is saved in the account's row and only a signed-in screen reads it (the signed-out
   landing page has no language). The device kept one copy and a sign-out does not reload the page, so
   the next account opened in the last one's language and direction until its own load — and kept them
   when its row had none; and a late answer for the last account switched the next one's screen. */
scopeToAccount(useLanguageStore);
